import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'primary' | 'error' | 'warning';
}

interface FeedbackContextType {
  // Toast notification. Replaces window.alert() for non-blocking messages.
  notify: (message: string, severity?: AlertColor) => void;
  // Promise-based confirmation. Replaces window.confirm().
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export const useFeedback = (): FeedbackContextType => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within a FeedbackProvider');
  return ctx;
};

interface SnackState {
  open: boolean;
  message: string;
  severity: AlertColor;
  key: number;
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve?: (value: boolean) => void;
}

export const FeedbackProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: '',
    severity: 'info',
    key: 0,
  });
  const [dialog, setDialog] = useState<ConfirmState>({ open: false, message: '' });

  const notify = useCallback((message: string, severity: AlertColor = 'info') => {
    setSnack({ open: true, message, severity, key: Date.now() });
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setDialog({ ...options, open: true, resolve });
    });
  }, []);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const settleDialog = (result: boolean) => {
    dialog.resolve?.(result);
    setDialog((d) => ({ ...d, open: false }));
  };

  return (
    <FeedbackContext.Provider value={{ notify, confirm }}>
      {children}

      <Snackbar
        key={snack.key}
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Dialog open={dialog.open} onClose={() => settleDialog(false)}>
        {dialog.title && <DialogTitle>{dialog.title}</DialogTitle>}
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{dialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => settleDialog(false)} color="inherit">
            {dialog.cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={() => settleDialog(true)}
            color={dialog.severity === 'error' ? 'error' : dialog.severity === 'warning' ? 'warning' : 'primary'}
            variant="contained"
            autoFocus
          >
            {dialog.confirmText || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
};
